import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { carHouseService } from '../../../services/carHouseService';

const DealershipModal = ({ 
  isOpen, 
  onClose, 
  dealership, 
  onSave, 
  mode = 'view' // 'view', 'edit', 'create'
}) => {
  const [formData, setFormData] = useState({
    name: '',
    street: '',
    zipCode: '',
    city: '',
    contactPerson: '',
    email: '',
    phone: '',
    vatId: '',
    notes: ''
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [stats, setStats] = useState({
    activeOrders: 0,
    completedOrders: 0,
    totalRevenue: 0,
    averageRating: 0
  });
  const [isLoadingStats, setIsLoadingStats] = useState(false);

  useEffect(() => {
    if (dealership && (mode === 'view' || mode === 'edit')) {
      setFormData({
        name: dealership?.name || '',
        street: dealership?.street || '',
        zipCode: dealership?.zipCode || '',
        city: dealership?.city || '',
        contactPerson: dealership?.contactPerson || '',
        email: dealership?.email || '',
        phone: dealership?.phone || '',
        vatId: dealership?.vatId || '',
        notes: dealership?.notes || ''
      });
    } else if (mode === 'create') {
      setFormData({
        name: '',
        street: '',
        zipCode: '',
        city: '',
        contactPerson: '',
        email: '',
        phone: '',
        vatId: '',
        notes: ''
      });
    }
    setErrors({});
    setSaveError(null);
  }, [dealership, mode, isOpen]);

  // Load statistics when viewing a dealership
  const loadStats = async () => {
    if (mode === 'view' && dealership?.id) {
      setIsLoadingStats(true);
      try {
        const { data, error } = await carHouseService.getCarHouseStats(dealership.id);
        if (error) {
          console.error('Error loading stats:', error);
        } else {
          setStats(data);
        }
      } catch (err) {
        console.error('Error loading stats:', err);
      } finally {
        setIsLoadingStats(false);
      }
    }
  };

  useEffect(() => {
    loadStats();
  }, [dealership?.id, mode]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = 'Name ist erforderlich';
    }
    if (!formData?.city?.trim()) {
      newErrors.city = 'Stadt ist erforderlich';
    }
    if (!formData?.zipCode?.trim()) {
      newErrors.zipCode = 'PLZ ist erforderlich';
    }
    if (!formData?.contactPerson?.trim()) {
      newErrors.contactPerson = 'Ansprechpartner ist erforderlich';
    }
    if (!formData?.email?.trim()) {
      newErrors.email = 'E-Mail ist erforderlich';
    } else if (!/\S+@\S+\.\S+/?.test(formData?.email)) {
      newErrors.email = 'Ungültige E-Mail-Adresse';
    }
    if (!formData?.phone?.trim()) {
      newErrors.phone = 'Telefon ist erforderlich';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSave = async () => {
    if (mode === 'view') return;

    if (!validateForm()) return;

    setIsLoading(true);
    setSaveError(null);
    try {
      await onSave(formData);
      onClose();
    } catch (error) {
      console.error('Error saving dealership:', error);
      setSaveError(error.message || 'Fehler beim Speichern des Autohauses');
    } finally {
      setIsLoading(false);
    }
  };



  const getModalTitle = () => {
    switch (mode) {
      case 'create':
        return 'Neues Autohaus';
      case 'edit':
        return 'Autohaus bearbeiten';
      case 'view':
      default:
        return 'Autohaus Details';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-1200 p-4">
      <div className="bg-card rounded-lg border border-border w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
              <Icon name="Building2" size={20} color="white" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">{getModalTitle()}</h2>
              {dealership && mode === 'view' && (
                <p className="text-sm text-muted-foreground">ID: {dealership?.id}</p>
              )}
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
          >
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* Error Display */}
          {saveError && (
            <div className="mb-6 p-4 bg-error/10 border border-error/20 rounded-lg">
              <div className="flex items-center space-x-2">
                <Icon name="AlertCircle" size={20} className="text-error" />
                <p className="text-error font-medium">Fehler beim Speichern</p>
              </div>
              <p className="text-error/80 text-sm mt-1">{saveError}</p>
            </div>
          )}
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-foreground border-b border-border pb-2">
                Grundinformationen
              </h3>
              
              <Input
                label="Name des Autohauses"
                type="text"
                value={formData?.name}
                onChange={(e) => handleInputChange('name', e?.target?.value)}
                error={errors?.name}
                disabled={mode === 'view'}
                required
              />
            </div>

            {/* Address Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-foreground border-b border-border pb-2">
                Adresse
              </h3>
              
              <Input
                label="Straße und Hausnummer"
                type="text"
                value={formData?.street}
                onChange={(e) => handleInputChange('street', e?.target?.value)}
                disabled={mode === 'view'}
              />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input
                  label="PLZ"
                  type="text"
                  value={formData?.zipCode}
                  onChange={(e) => handleInputChange('zipCode', e?.target?.value)}
                  error={errors?.zipCode}
                  disabled={mode === 'view'}
                  required
                />
                <Input
                  label="Stadt"
                  type="text"
                  value={formData?.city}
                  onChange={(e) => handleInputChange('city', e?.target?.value)}
                  error={errors?.city}
                  disabled={mode === 'view'}
                  required
                />
              </div>
            </div>

            {/* Contact Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-foreground border-b border-border pb-2">
                Kontaktdaten
              </h3>
              
              <Input
                label="Ansprechpartner"
                type="text"
                value={formData?.contactPerson}
                onChange={(e) => handleInputChange('contactPerson', e?.target?.value)}
                error={errors?.contactPerson}
                disabled={mode === 'view'}
                required
              />

              <Input
                label="E-Mail"
                type="email"
                value={formData?.email}
                onChange={(e) => handleInputChange('email', e?.target?.value)}
                error={errors?.email}
                disabled={mode === 'view'}
                required
              />

              <Input
                label="Telefon"
                type="tel"
                value={formData?.phone}
                onChange={(e) => handleInputChange('phone', e?.target?.value)}
                error={errors?.phone}
                disabled={mode === 'view'}
                required
              />
            </div>

            {/* Business Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-foreground border-b border-border pb-2">
                Geschäftsdaten
              </h3>
              
              <Input
                label="USt-IdNr."
                type="text"
                value={formData?.vatId}
                onChange={(e) => handleInputChange('vatId', e?.target?.value)}
                disabled={mode === 'view'}
                placeholder="DE123456789"
              />

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Notizen
                </label>
                <textarea
                  value={formData?.notes}
                  onChange={(e) => handleInputChange('notes', e?.target?.value)}
                  disabled={mode === 'view'}
                  rows={4}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground disabled:bg-muted disabled:text-muted-foreground resize-none"
                  placeholder="Zusätzliche Informationen..."
                />
              </div>
            </div>
          </div>

                                {/* Service History (View Mode Only) */}
            {mode === 'view' && dealership && (
              <div className="mt-8 pt-6 border-t border-border">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-foreground">
                    Service-Übersicht
                  </h3>
                                     <Button
                     variant="outline"
                     size="sm"
                     onClick={loadStats}
                     loading={isLoadingStats}
                   >
                     <Icon name="RefreshCw" size={16} className={`mr-2 ${isLoadingStats ? 'animate-spin' : ''}`} />
                     Aktualisieren
                   </Button>
                </div>
                {isLoadingStats ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="w-8 h-8 bg-muted rounded-full flex items-center justify-center">
                      <Icon name="Loader2" size={20} className="text-muted-foreground animate-spin" />
                    </div>
                    <span className="ml-2 text-muted-foreground">Lade Statistiken...</span>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="bg-muted rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-primary">{stats.activeOrders}</div>
                      <div className="text-sm text-muted-foreground">Aktive Aufträge</div>
                    </div>
                    <div className="bg-muted rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-success">{stats.completedOrders}</div>
                      <div className="text-sm text-muted-foreground">Abgeschlossen</div>
                    </div>
                    <div className="bg-muted rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-accent">€ {stats.totalRevenue.toLocaleString()}</div>
                      <div className="text-sm text-muted-foreground">Gesamtumsatz</div>
                    </div>
                    <div className="bg-muted rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-warning">
                        {stats.averageRating > 0 ? stats.averageRating : '-'}
                      </div>
                      <div className="text-sm text-muted-foreground">Bewertung</div>
                    </div>
                  </div>
                )}
              </div>
            )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-border">
          <Button
            variant="outline"
            onClick={onClose}
          >
            {mode === 'view' ? 'Schließen' : 'Abbrechen'}
          </Button>
          {mode !== 'view' && (
            <Button
              variant="default"
              onClick={handleSave}
              loading={isLoading}
            >
              <Icon name="Save" size={16} className="mr-2" />
              {mode === 'create' ? 'Erstellen' : 'Speichern'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DealershipModal;